/**
 * Permission Utilities
 * Cross-platform permission handling for microphone access
 */

import { Platform, PermissionsAndroid, Linking } from "react-native";
import { createLogger } from "./logger";
import { useAppBannerStore } from "../hooks/useAppBanner";

const log = createLogger("Permissions");

export type PermissionStatus =
  | "granted"
  | "denied"
  | "blocked"
  | "unavailable"
  | "unknown";

/**
 * Request microphone permission
 * Returns the permission status after request
 */
export async function requestMicrophonePermission(): Promise<PermissionStatus> {
  if (Platform.OS === "web") {
    return requestMicrophonePermissionWeb();
  } else if (Platform.OS === "android") {
    return requestMicrophonePermissionAndroid();
  } else {
    // iOS requests permission automatically when native module accesses microphone
    // We can't pre-request, but we can check after denial
    return "granted";
  }
}

/**
 * Request microphone permission on Android
 */
async function requestMicrophonePermissionAndroid(): Promise<PermissionStatus> {
  try {
    // Check if already granted
    const hasPermission = await PermissionsAndroid.check(
      PermissionsAndroid.PERMISSIONS.RECORD_AUDIO
    );

    if (hasPermission) {
      return "granted";
    }

    // Request permission
    const result = await PermissionsAndroid.request(
      PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
      {
        title: "Microphone Permission",
        message:
          "Fluencia needs access to your microphone for voice recording and pronunciation assessment.",
        buttonNeutral: "Ask Me Later",
        buttonNegative: "Cancel",
        buttonPositive: "OK",
      }
    );

    switch (result) {
      case PermissionsAndroid.RESULTS.GRANTED:
        return "granted";
      case PermissionsAndroid.RESULTS.DENIED:
        return "denied";
      case PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN:
        return "blocked";
      default:
        return "unknown";
    }
  } catch (err: unknown) {
    log.error("Error requesting microphone permission", err);
    return "unknown";
  }
}

/**
 * Request microphone permission on Web
 */
async function requestMicrophonePermissionWeb(): Promise<PermissionStatus> {
  try {
    // Check if navigator.mediaDevices is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      log.warn("MediaDevices API not available");
      return "unavailable";
    }

    // Try to get a media stream to trigger permission prompt
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Permission granted - stop the stream immediately
    stream.getTracks().forEach((track) => track.stop());

    return "granted";
  } catch (err: unknown) {
    const error = err as { name?: string; message?: string };

    if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
      return "denied";
    } else if (error.name === "NotFoundError") {
      return "unavailable";
    }

    log.error("Unexpected web microphone permission error", err);
    return "unknown";
  }
}

/**
 * Check if microphone permission is granted (without requesting)
 * Note: This only works reliably on Android. iOS/Web may require actually trying to use the mic.
 */
export async function checkMicrophonePermission(): Promise<PermissionStatus> {
  if (Platform.OS === "android") {
    try {
      const hasPermission = await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.RECORD_AUDIO
      );
      return hasPermission ? "granted" : "denied";
    } catch (err: unknown) {
      log.error("Error checking microphone permission", err);
      return "unknown";
    }
  } else if (Platform.OS === "web") {
    // Web Permissions API (if available)
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        switch (result.state) {
          case "granted":
            return "granted";
          case "denied":
            return "blocked";
          case "prompt":
            return "denied"; // Needs to be requested
          default:
            return "unknown";
        }
      }
    } catch {
      // Permissions API not supported for microphone
    }
    return "unknown";
  }

  // iOS - assume granted until proven otherwise
  return "granted";
}

/**
 * Show banner for denied permission.
 * Settings navigation is handled by the calling component if needed.
 */
export function showPermissionDeniedAlert(
  _onRetry?: () => void
): void {
  useAppBannerStore.getState().showBanner({
    message:
      "Fluencia needs microphone access for voice recording. Please enable microphone permission in your device settings.",
    type: "error",
    action:
      Platform.OS !== "web"
        ? {
            label: "Open Settings",
            onPress: () => {
              if (Platform.OS === "ios") {
                Linking.openURL("app-settings:");
              } else {
                Linking.openSettings();
              }
            },
          }
        : undefined,
  });
}

/**
 * Show banner for blocked permission (never ask again on Android)
 */
export function showPermissionBlockedAlert(): void {
  useAppBannerStore.getState().showBanner({
    message:
      "You have permanently denied microphone access. To enable voice recording, please go to your device settings and enable microphone permission for Fluencia.",
    type: "error",
    action: {
      label: "Open Settings",
      onPress: () => {
        if (Platform.OS === "ios") {
          Linking.openURL("app-settings:");
        } else {
          Linking.openSettings();
        }
      },
    },
  });
}

/**
 * Show banner for unavailable microphone
 */
export function showMicrophoneUnavailableAlert(): void {
  useAppBannerStore.getState().showBanner({
    message:
      "No microphone was detected on your device. Voice recording requires a working microphone.",
    type: "error",
  });
}
